import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { Tag } from '@shared/components/tags/model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { TagEditorWrapperComponent } from '@shared/components/tag-editor-wrapper/tag-editor-wrapper.component';
import { ICellRendererParams } from 'ag-grid-enterprise';

/**
 * AG Grid cell EDITOR for tags. Rendered as a popup when a Tags cell enters edit mode.
 * Implements ICellEditorAngularComp so ag-grid can use it as a proper cell editor.
 *
 * Uses TagEditorWrapperComponent to handle tag management logic.
 *
 * Column definition usage:
 *   customCellEditor: 'agTagComponent'
 */
@Component({
  selector: 'miq-ag-tag-editor',
  templateUrl: 'ag-tag-editor.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TagEditorWrapperComponent],
})
export class AgTagEditorComponent implements ICellEditorAngularComp {
  public tags: Tag[] = [];
  public canEdit = false;
  public filterUrl: string | null = null;
  public entityId: number | string | null = null;

  private params: ICellRendererParams | any;
  private originalTags: Tag[] = [];

  constructor(private userInfoResolver: UserInfoResolver) {}

  agInit(params: any): void {
    this.params = params;
    this.tags = this.parseTags(params.value);
    this.originalTags = JSON.parse(JSON.stringify(this.tags)); // Deep copy
    this.filterUrl = params.colDef?.lookupCategory;
    this.entityId = params.node?.data?.entity?.id;

    this.userInfoResolver.isTrustedUser().then(trusted => {
      this.canEdit = params?.editable && trusted;
    });
  }

  /** Called when tags are updated in the wrapper */
  public onTagsChanged(updatedTags: Tag[]): void {
    this.tags = updatedTags;
  }

  // AG Grid reads the final value from here when the editor closes
  public getValue(): any {
    return this.tags;
  }

  /** Refresh the cell display after the editor closes. */
  public destroy(): void {
    if (this.params?.api) {
      this.params.api.refreshCells({
        rowNodes: [this.params.node],
        force: true,
      });
    }
  }

  /** Render as a popup so the tag chips have room to display */
  isPopup(): boolean {
    return true;
  }

  private parseTags(value: any): Tag[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((tag, _index) => ({
        id: tag.id ?? null,
        name: tag.name ?? String(tag),
      }));
    }
    return [];
  }
}
